import React, {useEffect, useState, useCallback, useMemo, useRef} from "react";
import "./App.css";
import {Route, Switch, useHistory, useLocation} from "react-router-dom";
import Routes from "../routing/Routes";
import {
    AuthActions,
    AuthGState,
    darkTheme,
    Footer,
    getAllOrgsIdsForUserByServiceName,
    MainLoader,
    Navbar,
    setAlert,
    theme,
    useFavoriteTheme,
    ThemeProvider,
    CssBaseline,
    Logout,
    ADTE_LOGO_SMALL_URL
} from "@leap4/frontend"
import {connect, ConnectedProps, useDispatch} from "react-redux";
import {BACKEND_SERVICE_NAME, logoutUrl, meMultiUrl, requiredAuthRoles,} from "../../config/generals";
import {RootState} from "../../interfaces/root-state.interface";
import {urlService} from "../routing/urlsService";
import {QueryClient, QueryClientProvider, useQueryClient} from "react-query";
import {setIsLoading} from "../../store/actions/dateRange.actions";
import {isPartyModeUser, tagPartyModeNavLink} from "./partyMode";



const mapStateToProps = (state: RootState) => ({
    auth: state.auth,
    selectedOrganization: state.auth.selectedOrganizationId
});

const mapActions = {
    setIsLoading,
    setAlert
}

const connector = connect(mapStateToProps, mapActions);
type PropsFromRedux = ConnectedProps<typeof connector>;


interface IProps extends PropsFromRedux {
    history: History,
    auth: AuthGState
}
interface LinksType {
    text: string;
    link: string | LinksType[];
}

const queryClient = new QueryClient();

// Inner component that has access to QueryClient context (must be inside QueryClientProvider)
const AppInner = ({auth, selectedOrganization, dispatch, history, location}: {
    auth: AuthGState;
    selectedOrganization: string;
    dispatch: any;
    history: any;
    location: any;
}) => {
    const qc = useQueryClient();

    // Track previous organization to detect actual changes
    const prevOrgRef = useRef<string | null>(null);
    
    useEffect(() => {
        // On first mount, just record the current org
        if (prevOrgRef.current === null) {
            prevOrgRef.current = selectedOrganization;
            return;
        }
        
        // If org actually changed (not just on every render)
        if (selectedOrganization && prevOrgRef.current !== selectedOrganization) {
            prevOrgRef.current = selectedOrganization;
            
            // Invalidate all queries - they'll refetch when components mount/are visible
            qc.invalidateQueries();
            
            console.log('Organization changed, cache invalidated');
        }
    }, [selectedOrganization, qc]);

    return null; // This component only handles side effects
};

const App = ({auth, selectedOrganization}: IProps) => {
    const history = useHistory();
    const [selectedTheme, setSelectedTheme] = useFavoriteTheme()
    const location = useLocation();
    const dispatch = useDispatch();
    const [bIsSidebarOpen, SetSideBarOpenState] = useState(false);
    const [isLoading, setIsLoading] = useState(true)
    const [currentUserId, setCurrentUserId] = useState('')

    const nSidebarWidth = 240;
    
    // Styles migrated from makeStyles to sx prop pattern
    const styles = {
        root: {
            display: "flex",
            flexDirection: "column" as const,
            minHeight: "100vh",
        },
        toolbar: {
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
        },
        content: {
            flexGrow: 1,
            width: "100%",
            margin: "auto",
            paddingTop: "30px", // Account for fixed navbar height
        },
    };

    // Memoize organizations to prevent new array reference on every render
    const userOrganizations = React.useMemo(() => {
        if (!auth.user) return [];
        return getAllOrgsIdsForUserByServiceName(auth.user, auth.user.organizations as string[], requiredAuthRoles, BACKEND_SERVICE_NAME);
    }, [auth.user?._id]); // Only recalculate when user ID changes

    const handleThemeChange = useCallback((checked) => {
        // checked=true means dark mode (moon highlighted), checked=false means light mode (sun highlighted)
        const themeToShow = checked ? darkTheme : theme;
        setSelectedTheme(themeToShow)
    }, [setSelectedTheme]);

    // Memoize Navbar props to prevent new references on every render
    const handleDrawerClose = useCallback(() => SetSideBarOpenState(false), []);
    const handleDrawerOpen = useCallback(() => SetSideBarOpenState(true), []);
    
    const navbarAuthProp = useMemo(() => ({ isAuthenticated: true }), []);
    const navbarUiProp = useMemo(() => ({ nSidebarWidth, nNavbarHeight: 78 }), []);
    
    const logoutProp = useMemo(() => ({
        text: "Logout",
        action: async () => await AuthActions.logout(dispatch, history, logoutUrl),
        icon: <Logout/>
    }), [dispatch, history]);

    useEffect(() => {
        (async () => {
                try {
                    await AuthActions.loadUser(dispatch, BACKEND_SERVICE_NAME, meMultiUrl)
                } finally {
                    setIsLoading(false)
                }
            }
        )();
    }, []);


    useEffect(() => {
        if (auth.user?._id && auth.user._id !== currentUserId) {
            setCurrentUserId(auth.user._id)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [auth.user?._id]) // Only re-run when user ID changes, not on every auth object change

    const navbarLinks: LinksType[] = useMemo(() => [
        {
            text: "Settings", link: [
                { text: "Organization", link: urlService.sites() },
                { text: "Users", link: urlService.users() },
                { text: "Integrations", link: urlService.integrations() }
            ]
        },
        // Flattened out of a "Tools" dropdown: these are the day-to-day screens, and burying
        // them one level down made them feel optional. Settings stays grouped because it's
        // configuration you visit rarely.
        { text: "Sellers.json", link: urlService.sellers() },
        { text: "Buyers", link: urlService.demands() },
        { text: "Query Hub", link: urlService.demandWLGenerator() },
        { text: "Publisher Lines", link: urlService.appAdsLines() },
        { text: "Apps Data", link: urlService.appsData() },
    ], []);

    /**
     * Party mode (see partyMode.ts) - lights up the Publisher Lines nav item on hover, for a
     * couple of named accounts only.
     *
     * The dependency list is doing real work. The navbar's buttons are styled(Button), so
     * emotion mints a new class name whenever the theme changes or an item becomes the active
     * route; React then writes that fresh className to the node and drops the class we added
     * out of band. Re-tagging on theme and path changes puts it back. `isLoading` is in there
     * because the navbar only mounts once it clears, and the extra frame covers the case where
     * the user load and the loading flag settle in separate commits.
     */
    useEffect(() => {
        const party = isPartyModeUser(auth.user?.email);
        tagPartyModeNavLink(party);
        const raf = requestAnimationFrame(() => tagPartyModeNavLink(party));
        return () => cancelAnimationFrame(raf);
    }, [auth.user?.email, isLoading, location.pathname, selectedTheme]);

    if (isLoading) {
        return <MainLoader/>
    }


    return (
        <>
            <ThemeProvider theme={selectedTheme}>
                <QueryClientProvider client={queryClient}>
                    {/* Inner component handles org change cache invalidation */}
                    <AppInner 
                        auth={auth} 
                        selectedOrganization={selectedOrganization} 
                        dispatch={dispatch} 
                        history={history} 
                        location={location} 
                    />
                    <div style={styles.root}>
                        <CssBaseline/>
                        {auth?.user && <Navbar
                            bIsSidebarOpen={bIsSidebarOpen}
                            handleDrawerClose={handleDrawerClose}
                            handleDrawerOpen={handleDrawerOpen}
                            auth={navbarAuthProp}
                            ui={navbarUiProp}
                            location={location.pathname}
                            links={navbarLinks}
                            orgsWithPermssions={userOrganizations}
                            updateCheckedToFather={handleThemeChange}
                            title={'Adte PAPA-2'}
                            logoImageUrl={ADTE_LOGO_SMALL_URL}
                            logout={logoutProp}
                        />}


                        <main style={styles.content}>
                            <div style={styles.toolbar}/>
                            <Switch>
                                <Route component={Routes}/>{" "}
                            </Switch>
                        </main>
                        <Footer text={'Papa'}
                                logoUrl={ADTE_LOGO_SMALL_URL}/>
                    </div>
                </QueryClientProvider>
            </ThemeProvider>

        </>
    );
};


export default connector(App);

